/* What must never reach the error tracker — `security.md`, `data-map.md`.
 *
 * Shared by the API and the web client because they report to the same
 * processor, and a redaction list that differs between two surfaces is a list
 * where one of them is wrong. `documentation.md`: one fact, one home.
 *
 * Deliberately dependency-free and structurally typed. Both Sentry SDKs pass
 * the same event shape, but importing either one here would make this package
 * pick a runtime — and the thing worth sharing is the JUDGEMENT about what is
 * sensitive, not the plumbing of one SDK.
 *
 * ── what this deliberately does NOT do ───────────────────────────────────────
 * It does not try to recognise names, dietary requirements, or the free-text
 * fields `data-map.md` marks as possibly special-category. Nothing reliably
 * can. The protection for those is that they are never put in an error message
 * in the first place — this is the net under that rule, not a replacement.
 */
/* `security.md`'s redaction list, as far as a key name can express it. Matched
 * on the key, case-insensitively, at any depth.
 *
 * A safety net and NOT a licence to hand secrets to the reporter and trust it
 * to catch them. It cannot see a token concatenated into a message string, and
 * it cannot know that `value` holds a card number. */
export const REDACTED_KEYS = /password|token|secret|authorization|cookie|session|api[-_]?key|card|cvv|iban|ssn/i;
export const REDACTED = '[redacted]';
/* Matched on shape, for the values that arrive with no key to check.
 *
 * Deliberately broad. A false positive costs a `[redacted]` in a diagnostic; a
 * false negative sends a customer's address to a third party. */
const EMAIL = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
/* Long opaque strings in a URL or a message: session tokens, verification and
 * reset tokens, API keys. 24 is above any word and below every token we mint —
 * the API issues 32 random bytes as base64url, which is 43 characters. */
const LONG_OPAQUE = /\b[A-Za-z0-9_-]{24,}\b/g;
/* Identifiers, which are the diagnostic content rather than the risk.
 *
 * Every id in the product is a UUIDv7 (ADR-0009) and `-` is inside the class
 * above, so without this exemption the whole uuid matches and an issue reads
 * "event [redacted] not found" — true, useless, and the reason somebody
 * eventually turns the redactor off.
 *
 * Safe because it is the EXACT uuid shape: 8-4-4-4-12 hex. A minted token is 43
 * base64url characters and cannot match it. Asserted by a test rather than
 * reasoned about, because the exemption is only safe while that stays true. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Masks anything that LOOKS like a credential or an address. */
export function redactText(value) {
    return value
        .replace(EMAIL, '[email]')
        .replace(LONG_OPAQUE, (match) => (UUID.test(match) ? match : REDACTED));
}
/** Applies both rules — key names and value shapes — at any depth. */
export function redactDeep(value, depth = 0) {
    if (depth > 6)
        return value;
    if (typeof value === 'string')
        return redactText(value);
    if (Array.isArray(value))
        return value.map((entry) => redactDeep(entry, depth + 1));
    if (value === null || typeof value !== 'object')
        return value;
    const out = {};
    for (const [key, entry] of Object.entries(value)) {
        out[key] = REDACTED_KEYS.test(key) ? REDACTED : redactDeep(entry, depth + 1);
    }
    return out;
}
/**
 * The last thing that touches an event before it leaves the process.
 *
 * Pure, so it can be tested without a DSN, a network, or an initialised SDK —
 * which matters, because the only other way to find out whether this works is
 * to send real data to a third party and go and look.
 */
export function scrubEvent(event) {
    /* `T extends object` rather than `T extends ScrubbableEvent`, and the cast is
     * the reason: the SDKs' own `ErrorEvent` is far wider than the fields this
     * touches and does not structurally satisfy a narrower interface. Naming the
     * SDK type instead would make this package pick a runtime, which is exactly
     * what it exists not to do.
     *
     * The shape is still declared, and `redaction.test.ts` builds its events from
     * it — so the fields below are checked against something, and the return type
     * is the caller's own event rather than a widened one. */
    const scrubbed = { ...event };
    /* The request is the richest source of personal data on an event and almost
     * none of it is diagnostic. The METHOD and the path are kept; everything that
     * carries a value is dropped rather than cleaned — headers, cookies and the
     * body included, without asking what is in them. */
    if (scrubbed.request) {
        const { method, url } = scrubbed.request;
        scrubbed.request = {
            ...(method === undefined ? {} : { method }),
            /* Query string removed entirely. `?token=…` is the shape of every
             * verification and reset link in the product, so the safe rule is that a
             * query string never travels, not that we try to clean one. */
            ...(url === undefined ? {} : { url: redactText(url.split('?')[0] ?? url) }),
        };
    }
    /* Populated by the SDK from the session when `sendDefaultPii` is on. It is
     * off, and this is the belt to that braces: a `setUser` added elsewhere later
     * must not quietly start shipping addresses and IPs. An id is kept — it
     * identifies a row to us and nothing to the processor. */
    if (scrubbed.user) {
        scrubbed.user = scrubbed.user.id === undefined ? {} : { id: String(scrubbed.user.id) };
    }
    if (scrubbed.message !== undefined)
        scrubbed.message = redactText(scrubbed.message);
    if (scrubbed.exception?.values) {
        scrubbed.exception = {
            ...scrubbed.exception,
            values: scrubbed.exception.values.map((entry) => ({
                ...entry,
                ...(entry.value === undefined ? {} : { value: redactText(entry.value) }),
            })),
        };
    }
    if (scrubbed.breadcrumbs) {
        scrubbed.breadcrumbs = scrubbed.breadcrumbs.map((crumb) => ({
            ...crumb,
            ...(crumb.message === undefined ? {} : { message: redactText(crumb.message) }),
            ...(crumb.data === undefined ? {} : { data: redactDeep(crumb.data) }),
        }));
    }
    if (scrubbed.extra)
        scrubbed.extra = redactDeep(scrubbed.extra);
    if (scrubbed.contexts)
        scrubbed.contexts = redactDeep(scrubbed.contexts);
    if (scrubbed.tags)
        scrubbed.tags = redactDeep(scrubbed.tags);
    return scrubbed;
}
//# sourceMappingURL=redaction.js.map